import { createBrowserRouter, RouterProvider, Navigate } from 'react-router-dom';
import PrivateRoute from './PrivateRoute';
import PermissionRoute from './PermissionRoute';
import AppLayout from '../layouts/AppLayout';
import AuthLayout from '../layouts/AuthLayout';

import LoginPage         from '../modules/auth/pages/Login';
import ForgotPasswordPage from '../modules/auth/pages/ForgotPassword';
import ResetPasswordPage  from '../modules/auth/pages/ResetPassword';
import DashboardPage from '../modules/dashboard/pages/Dashboard';
import ClinicListPage from '../modules/clinic/pages/ClinicList';
import SchoolListPage from '../modules/school/pages/SchoolList';
import StaffListPage from '../modules/staff/pages/StaffList';
import DoctorListPage from '../modules/doctor/pages/DoctorList';
import ChildListPage from '../modules/child/pages/ChildList';
import ReferralListPage from '../modules/referral/pages/ReferralList';
import ReferralViewPage from '../modules/referral/pages/ReferralView';
import EventListPage from '../modules/event/pages/EventList';
import EventManagePage from '../modules/event/pages/EventManage';
import SmilepassportListPage from '../modules/event/pages/SmilepassportList';
import RegistrationListPage from '../modules/registration/pages/RegistrationList';
import CheckupListPage    from '../modules/checkup/pages/CheckupList';
import CheckupPage        from '../modules/checkup/pages/CheckupPage';
import ChildDetailsPage   from '../modules/checkup/pages/ChildDetailsPage';
import ReportPage       from '../modules/report/pages/ReportPage';
import PrivacyPolicy      from '../pages/PrivacyPolicy';
import TermsAndConditions from '../pages/TermsAndConditions';
import DeleteAccount      from '../pages/DeleteAccount';

// All roles constant for routes accessible to any authenticated user
const ANY_AUTHENTICATED = ['admin', 'school_staff', 'doctor'];

const RootRedirect = () => {
  return <Navigate to="/dashboard" replace />;
};

const router = createBrowserRouter([
  {
    path: '/',
    element: <RootRedirect />,
  },
  {
    element: <AuthLayout />,
    children: [
      { path: '/login',           element: <LoginPage /> },
      { path: '/forgot-password', element: <ForgotPasswordPage /> },
      { path: '/reset-password',  element: <ResetPasswordPage /> },
    ],
  },
  {
    // Admin-only routes
    element: <PrivateRoute allowedRoles={['admin']} />,
    children: [
      {
        element: <AppLayout />,
        children: [
          { path: '/clinics', element: <PermissionRoute resource="clinic"><ClinicListPage /></PermissionRoute> },
          { path: '/schools', element: <PermissionRoute resource="school"><SchoolListPage /></PermissionRoute> },
          { path: '/staff', element: <PermissionRoute resource="staff"><StaffListPage /></PermissionRoute> },
          { path: '/doctors', element: <PermissionRoute resource="doctor"><DoctorListPage /></PermissionRoute> },
        ],
      },
    ],
  },
  {
    // Doctor-only routes
    element: <PrivateRoute allowedRoles={['doctor']} />,
    children: [
      {
        element: <AppLayout />,
        children: [
          { path: '/checkups', element: <PermissionRoute resource="checkup"><CheckupListPage /></PermissionRoute> },
        ],
      },
    ],
  },
  {
    // Referred children: admin + all doctors (a doctor sees referrals they made,
    // plus any referred to their own clinic)
    element: <PrivateRoute allowedRoles={['admin', 'doctor']} />,
    children: [
      {
        element: <AppLayout />,
        children: [
          { path: '/referrals', element: <PermissionRoute resource="referral"><ReferralListPage /></PermissionRoute> },
          { path: '/referrals/:id', element: <PermissionRoute resource="referral"><ReferralViewPage /></PermissionRoute> },
        ],
      },
    ],
  },
  {
    // Children: admin, school_staff, doctor
    element: <PrivateRoute allowedRoles={['admin', 'school_staff', 'doctor']} />,
    children: [
      {
        element: <AppLayout />,
        children: [
          { path: '/children', element: <PermissionRoute resource="child"><ChildListPage /></PermissionRoute> },
        ],
      },
    ],
  },
  {
    // Checkup recording page: admin + doctor
    element: <PrivateRoute allowedRoles={['admin', 'doctor']} />,
    children: [
      {
        element: <AppLayout />,
        children: [
          { path: '/events/:eventId/checkup/:registrationId',   element: <CheckupPage /> },
          { path: '/events/:eventId/children/:registrationId',  element: <ChildDetailsPage /> },
          { path: '/events/:eventId/smilepassport/:childId', element: <SmilepassportListPage /> },
        ],
      },
    ],
  },
  {
    // Routes accessible by any authenticated user
    element: <PrivateRoute allowedRoles={ANY_AUTHENTICATED} />,
    children: [
      {
        element: <AppLayout />,
        children: [
          { path: '/dashboard',     element: <PermissionRoute resource="dashboard"><DashboardPage /></PermissionRoute> },
          { path: '/events',        element: <PermissionRoute resource="event"><EventListPage /></PermissionRoute> },
          { path: '/events/:id',    element: <PermissionRoute resource="event"><EventManagePage /></PermissionRoute> },
          { path: '/registrations', element: <PermissionRoute resource="registration"><RegistrationListPage /></PermissionRoute> },
          { path: '/reports',       element: <PermissionRoute resource="report"><ReportPage /></PermissionRoute> },
        ],
      },
    ],
  },
  {
    path: '/privacy-policy',
    element: <PrivacyPolicy />,
  },
  {
    path: '/terms-and-conditions',
    element: <TermsAndConditions />,
  },
  {
    path: '/delete-account',
    element: <DeleteAccount />,
  },
  {
    path: '/unauthorized',
    element: (
      <div style={{ textAlign: 'center', padding: '100px' }}>
        <h1>403 - Unauthorized</h1>
        <p>You do not have permission to access this page.</p>
        <a href="/dashboard">Go to Dashboard</a>
      </div>
    ),
  },
  {
    path: '*',
    element: (
      <div style={{ textAlign: 'center', padding: '100px' }}>
        <h1>404 - Page Not Found</h1>
      </div>
    ),
  },
]);

const AppRoutes = () => <RouterProvider router={router} />;
export default AppRoutes;